/**
 * Portal v2 dashboard page (`/portal/` landing).
 *
 * Mirrors the partner-side MspOverviewV2Page idiom: SectionHeader + 4 KPI
 * tiles (Sparkline accessories on emails + pass rate, derived from existing
 * /portal/trends data) + 2 quick-action CardV2s. Recent activity surfaces
 * conditionally below.
 *
 * View-only — no add-domain CTA, no config buttons.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  CheckCircle2,
  FileText,
  Globe,
} from "lucide-react";
import { Button, Skeleton } from "@ddmarc/ui";
import { ExportMenu } from "@/components/v2/export-menu";
import { portalApi } from "@/lib/portal-api";
import { useBranding } from "@/lib/branding-context";
import { usePortalScope } from "@/lib/portal-scope-context";
import {
  CardV2,
  KpiTile,
  SectionHeader,
  Sparkline,
} from "@/components/primitives";

export function PortalDashboardV2Page() {
  const { branding } = useBranding();
  const { selectedDomainId, selectedDomain } = usePortalScope();

  const { data: profile } = useQuery({
    queryKey: ["portal", "profile"],
    queryFn: portalApi.getProfile,
  });

  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ["portal", "dashboard", selectedDomainId],
    queryFn: () => portalApi.getDashboard({ domain_id: selectedDomainId }),
  });

  // Trends are decorative on the KPIs. A failed/empty fetch must not block
  // the page — fall through to no-sparkline rendering.
  const { data: trends } = useQuery({
    queryKey: ["portal", "trends", { period_days: 30 }, selectedDomainId],
    queryFn: () =>
      portalApi.getTrends({ period_days: 30, domain_id: selectedDomainId }),
    staleTime: 5 * 60 * 1000,
  });

  const emailsSeries = trends?.data.map((d) => d.total_emails) ?? [];
  const passRateSeries = trends?.data.map((d) => d.pass_rate) ?? [];

  const totalEmails30d = emailsSeries.reduce((sum, n) => sum + n, 0);
  const passRate = dashboard?.pass_rate ?? 0;
  const alertsCount = dashboard?.alerts_count ?? 0;
  const greetingName =
    profile?.customer_name || branding?.msp_name || "your portal";

  return (
    <div className="space-y-3.5">
      <SectionHeader
        title={`Welcome back to ${greetingName}`}
        subtitle={
          selectedDomain
            ? `DMARC posture and recent activity for ${selectedDomain.domain}.`
            : "DMARC posture and recent activity across all your domains."
        }
        actions={<ExportMenu />}
      />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Domains"
          value={
            dashboardLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              (dashboard?.domains_count ?? 0).toLocaleString()
            )
          }
          hint="Monitored"
        />
        <KpiTile
          label="Emails processed"
          value={
            dashboardLoading ? (
              <Skeleton className="h-7 w-20" />
            ) : (
              totalEmails30d.toLocaleString()
            )
          }
          hint="Last 30 days"
          accessory={
            emailsSeries.length >= 2 ? (
              <Sparkline points={emailsSeries} />
            ) : undefined
          }
        />
        <KpiTile
          label="Pass rate"
          value={
            dashboardLoading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              <span
                style={{
                  color: passRate < 70 ? "var(--v2-warn)" : undefined,
                }}
              >
                {passRate.toFixed(1)}%
              </span>
            )
          }
          hint="Across all domains"
          accessory={
            passRateSeries.length >= 2 ? (
              <Sparkline points={passRateSeries} />
            ) : undefined
          }
        />
        <KpiTile
          label="Open alerts"
          value={
            dashboardLoading ? (
              <Skeleton className="h-7 w-12" />
            ) : (
              <span
                style={{
                  color: alertsCount > 0 ? "var(--v2-warn)" : undefined,
                }}
              >
                {alertsCount.toLocaleString()}
              </span>
            )
          }
          hint={alertsCount > 0 ? "Needs attention" : "All clear"}
        />
      </div>

      <div className="grid gap-3.5 md:grid-cols-2">
        <CardV2
          title={
            <span className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
              Domains
            </span>
          }
          subtitle="Review monitored domains and their authentication status."
        >
          <Button asChild variant="outline" className="w-full">
            <Link to="/portal/domains">
              View domains
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardV2>

        <CardV2
          title={
            <span className="flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-muted-foreground" />
              Alerts
            </span>
          }
          subtitle={
            alertsCount > 0
              ? `${alertsCount.toLocaleString()} alert${alertsCount === 1 ? "" : "s"} need attention.`
              : "No open alerts. You're all clear."
          }
        >
          <Button
            asChild
            variant={alertsCount > 0 ? "default" : "outline"}
            className="w-full"
          >
            <Link to="/portal/alerts">
              {alertsCount > 0 ? "Review alerts" : "View alert log"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardV2>
      </div>

      {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
        <CardV2
          title={
            <span className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              Recent activity
            </span>
          }
        >
          <ul className="space-y-3">
            {dashboard.recent_activity.map((activity) => (
              <li
                key={activity.id}
                className="flex items-start gap-3 text-[12.5px]"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                  {activity.message?.toLowerCase().includes("fail") ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-[var(--v2-warn)]" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 text-[var(--v2-good)]" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground">{activity.message}</p>
                  <p className="text-[11.5px] text-muted-foreground">
                    {format(new Date(activity.created_at), "MMM d, h:mm a")}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </CardV2>
      )}
    </div>
  );
}
